import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ArrowLeft, CheckCircle2, Circle, Gauge, Loader2, Tag, Timer, UserCheck, Zap } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ProjectStatusesSection } from './ProjectStatusesSection'
import { SuperAgentSection } from './SuperAgentSection'
import { GitHubConnectSection } from './GitHubConnectSection'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { EstimationType } from '@/lib/types/estimation'
import type { UpdateProjectSettingsRequest } from '@/lib/api/features/project-settings/types'
import { useProjectMutations, useTasks  } from '@/lib/db/rxdb/hooks'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { UnifiedIconPicker } from '@/components/app/pickers/unified-icon-picker'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { ESTIMATION_SCALES } from '@/lib/types/estimation'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  disableProjectAdvancedStatus,
  enableProjectAdvancedStatus,
  getProjectSettings,
  updateProjectSettings,
} from '@/lib/api/features/project-settings'
import useLoginStore from '@/store/LoginStore'
import { useProjectStatusMode } from '@/hooks/useProjectStatusMode'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { PROGRESS_TYPES } from '@/lib/types/subscription'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { cn } from '@/lib/utils'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'

const onBadgeClass =
  'border-transparent bg-primary/10 text-primary text-[10px] font-semibold uppercase tracking-wide h-[18px] px-1.5 py-0'

const projectSettingsSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Project name is required')
      .max(50, 'Project name must be 50 characters or less'),
    estimationType: z.enum(['none', 'freeform', 'fibonacci', 'tshirt', 'exponential']),
    isWorkItemTypeEnabled: z.boolean(),
    isCyclesEnabled: z.boolean(),
    cycleDurationWeeks: z.number().int().min(1).max(52).nullable(),
    isAccountableEnabled: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.isCyclesEnabled && data.cycleDurationWeeks == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cycleDurationWeeks'],
        message: 'Cycle duration is required when cycles are enabled',
      })
    }
  })

type ProjectSettingsFormData = z.infer<typeof projectSettingsSchema>
type ProjectSettingsEstimationType = ProjectSettingsFormData['estimationType']

const ESTIMATION_TYPE_VALUES = new Set<ProjectSettingsEstimationType>([
  'none',
  'freeform',
  'fibonacci',
  'tshirt',
  'exponential',
])

function normalizeEstimationType(value: unknown): ProjectSettingsEstimationType {
  return typeof value === 'string' && ESTIMATION_TYPE_VALUES.has(value as ProjectSettingsEstimationType)
    ? (value as ProjectSettingsEstimationType)
    : 'none'
}

interface ProjectSettingsFormProps {
  project: ProjectDocument | null
  isLoading: boolean
  backLinkTo: string
  backLinkParams: Record<string, string>
}

export function ProjectSettingsForm({
  project,
  isLoading,
  backLinkTo,
  backLinkParams,
}: ProjectSettingsFormProps) {
  const { updateProject } = useProjectMutations()
  const workspaceSlug = useLoginStore((state) => state.workspaceSlug)
  const { isPersonal } = useWorkspaceMode()

  const [iconValue, setIconValue] = useState<string | null>(null)
  const [iconTypeValue, setIconTypeValue] = useState<string | null>(null)
  const [iconColorValue, setIconColorValue] = useState<string | null>(null)
  const [iconDirty, setIconDirty] = useState(false)

  const form = useForm<ProjectSettingsFormData>({
    resolver: zodResolver(projectSettingsSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      estimationType: 'none',
      isWorkItemTypeEnabled: false,
      isCyclesEnabled: false,
      cycleDurationWeeks: null,
      isAccountableEnabled: false,
    },
  })

  const applyProjectSettings = useCallback((settings: ProjectSettingsFormData) => {
    form.reset(settings)
  }, [form])

  useEffect(() => {
    if (!project || !workspaceSlug) return

    let cancelled = false

    ;(async () => {
      try {
        const settings = await getProjectSettings(workspaceSlug, project.id)
        if (cancelled || settings.id !== project.id) return

        applyProjectSettings({
          name: settings.name ?? '',
          estimationType: normalizeEstimationType(settings.estimationType),
          isWorkItemTypeEnabled: settings.isWorkItemTypeEnabled ?? false,
          isCyclesEnabled: settings.isCyclesEnabled ?? false,
          cycleDurationWeeks: settings.cycleDurationWeeks ?? null,
          isAccountableEnabled: settings.isAccountableEnabled ?? false,
        })
        setIconValue(settings.icon ?? null)
        setIconTypeValue(settings.iconType ?? null)
        setIconColorValue(settings.iconColor ?? null)
        setIconDirty(false)
      } catch (error) {
        logger.logError(error as Error, LogCategory.API_ERROR, {
          component: 'ProjectSettingsForm',
          action: 'load_project_settings',
          projectId: project.id,
        })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [project?.id, workspaceSlug, applyProjectSettings])

  const remoteName = project?.name
  const remoteEstimationType = project?.estimationType
  const remoteIsWorkItemTypeEnabled = project?.isWorkItemTypeEnabled
  const remoteIsCyclesEnabled = project?.isCyclesEnabled
  const remoteCycleDurationWeeks = project?.cycleDurationWeeks
  const remoteIsAccountableEnabled = project?.isAccountableEnabled
  const remoteIcon = project?.icon
  const remoteIconType = project?.iconType
  const remoteIconColor = project?.iconColor
  const isFormDirty = form.formState.isDirty

  useEffect(() => {
    if (!project) return
    // Don't clobber pending local edits; the GET-on-load effect handles the
    // initial population (and runs on project.id change), so this effect
    // exists purely to reconcile sync broadcasts arriving from other clients.
    if (isFormDirty || iconDirty) return

    applyProjectSettings({
      name: remoteName ?? '',
      estimationType: normalizeEstimationType(remoteEstimationType),
      isWorkItemTypeEnabled: remoteIsWorkItemTypeEnabled ?? false,
      isCyclesEnabled: remoteIsCyclesEnabled ?? false,
      cycleDurationWeeks: remoteCycleDurationWeeks ?? null,
      isAccountableEnabled: remoteIsAccountableEnabled ?? false,
    })
    setIconValue(remoteIcon ?? null)
    setIconTypeValue(remoteIconType ?? null)
    setIconColorValue(remoteIconColor ?? null)
  }, [
    project,
    remoteName,
    remoteEstimationType,
    remoteIsWorkItemTypeEnabled,
    remoteIsCyclesEnabled,
    remoteCycleDurationWeeks,
    remoteIsAccountableEnabled,
    remoteIcon,
    remoteIconType,
    remoteIconColor,
    isFormDirty,
    iconDirty,
    applyProjectSettings,
  ])

  const handleIconChange = useCallback(
    (value: { iconType: string | null; icon: string | null; iconColor?: string | null } | null) => {
      if (value) {
        setIconValue(value.icon)
        setIconTypeValue(value.iconType)
        setIconColorValue(value.iconColor ?? null)
      } else {
        setIconValue(null)
        setIconTypeValue(null)
        setIconColorValue(null)
      }
      setIconDirty(true)
    },
    [],
  )

  const onSubmit = async (data: ProjectSettingsFormData) => {
    if (!project || !workspaceSlug) return
    try {
      const request: UpdateProjectSettingsRequest = {
        name: data.name.trim(),
        icon: iconValue,
        iconType: iconTypeValue,
        iconColor: iconColorValue,
        // Estimation, work item types, and cycles are team-only — strip to
        // safe defaults on personal workspaces so the backend guard accepts
        // name/icon edits regardless of any pre-existing values on the project.
        estimationType: isPersonal ? 'none' : data.estimationType,
        isWorkItemTypeEnabled: isPersonal ? false : data.isWorkItemTypeEnabled,
        isCyclesEnabled: isPersonal ? false : data.isCyclesEnabled,
        cycleDurationWeeks: isPersonal ? null : data.cycleDurationWeeks,
        isAccountableEnabled: isPersonal ? false : data.isAccountableEnabled,
      }

      await updateProjectSettings(workspaceSlug, project.id, request)
      await updateProject(project.id, request)
      applyProjectSettings({
        ...data,
        name: request.name,
        cycleDurationWeeks: request.cycleDurationWeeks,
      })
      setIconDirty(false)
      toast.success('Project settings saved')
    } catch (error) {
      logger.logError(error as Error, LogCategory.API_ERROR, {
        component: 'ProjectSettingsForm',
        action: 'update_project_settings',
        projectId: project.id,
      })
      toast.error('Unable to save project settings')
    }
  }

  const isDirty = form.formState.isDirty || iconDirty

  if (isLoading) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center px-4">
        <div className="text-center text-muted-foreground">
          Project not found or may have been removed.
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex items-start justify-center w-full pt-8 pb-24 px-4">
          <div className="w-full max-w-2xl">
        {/* Back link */}
        <Link
          to={backLinkTo as any}
          params={backLinkParams as any}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          Back to project
        </Link>

        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <ProjectIcon iconType={iconTypeValue} icon={iconValue} iconColor={iconColorValue} size={20} />
          <h1 className="text-2xl font-semibold tracking-tight">
            {project.name} settings
          </h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Manage settings for this project
        </p>
        <Separator className="mb-8" />

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Name & Icon */}
          <div>
            <p className="text-sm font-medium mb-3">Name and icon</p>
            <div className="rounded-lg border bg-card p-4 flex items-center gap-3">
              <UnifiedIconPicker
                iconType={iconTypeValue}
                icon={iconValue}
                iconColor={iconColorValue}
                onValueChange={(value) => {
                  void handleIconChange(value)
                }}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label={iconValue ? 'Change project icon' : 'Add project icon'}
                >
                  <ProjectIcon iconType={iconTypeValue} icon={iconValue} iconColor={iconColorValue} size={18} />
                </Button>
              </UnifiedIconPicker>
              <div className="flex-1">
                <Input
                  {...form.register('name')}
                  placeholder="Project name"
                  className="h-9"
                />
                {form.formState.errors.name && (
                  <p className="text-xs text-destructive mt-1">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Estimation, work item types and cycles — team-only project settings */}
          {!isPersonal && (
          <div className="rounded-lg border bg-card overflow-hidden">
            <div
              className={cn(
                'flex items-start p-4 transition-colors',
                form.watch('estimationType') !== 'none' && 'bg-primary/[0.04]',
              )}
            >
              <div className="flex items-center gap-2 w-[55%] shrink-0 pt-0.5">
                <Gauge className="size-4 text-muted-foreground shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">Estimation</p>
                    {form.watch('estimationType') !== 'none' && (
                      <Badge className={onBadgeClass}>On</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    How effort is estimated on tasks in this project
                  </p>
                </div>
              </div>
              <div className="w-[45%]">
                <Controller
                  control={form.control}
                  name="estimationType"
                  render={({ field }) => {
                    const selectedValue = normalizeEstimationType(field.value)
                    return (
                      <Select
                        value={selectedValue}
                        onValueChange={(value) => {
                          form.setValue('estimationType', normalizeEstimationType(value), {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }}
                      >
                        <SelectTrigger className="h-9 w-full text-base md:text-sm">
                          <span className="truncate">{ESTIMATION_SCALES[selectedValue].label}</span>
                        </SelectTrigger>
                        <SelectContent>
                          {(
                            Object.entries(ESTIMATION_SCALES) as Array<[
                              EstimationType,
                              (typeof ESTIMATION_SCALES)[EstimationType],
                            ]>
                          ).map(([key, scale]) => (
                            <SelectItem key={key} value={key} className="text-sm">
                              {scale.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )
                  }}
                />
              </div>
            </div>

            {/* Work item types — hidden while the product is task > subtask only. */}
            {FEATURE_FLAGS.ENABLE_WORK_ITEM_TYPES && (
              <>
                <Separator />

                <div
                  className={cn(
                    'flex items-center p-4 transition-colors',
                    form.watch('isWorkItemTypeEnabled') && 'bg-primary/[0.04]',
                  )}
                >
                  <div className="flex items-center gap-2 w-[55%] shrink-0">
                    <Tag className="size-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">Work item types</p>
                        {form.watch('isWorkItemTypeEnabled') && (
                          <Badge className={onBadgeClass}>On</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Categorise tasks as bugs, features, and more
                      </p>
                    </div>
                  </div>
                  <div className="w-[45%] flex items-center gap-3">
                    <Switch
                      id="work-item-type-toggle"
                      checked={form.watch('isWorkItemTypeEnabled')}
                      onCheckedChange={(checked) => {
                        form.setValue('isWorkItemTypeEnabled', checked, { shouldDirty: true })
                      }}
                    />
                    <Label
                      htmlFor="work-item-type-toggle"
                      className="text-sm text-muted-foreground cursor-pointer"
                    >
                      {form.watch('isWorkItemTypeEnabled') ? 'Enabled' : 'Disabled'}
                    </Label>
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Cycles */}
            <div
              className={cn(
                'flex items-start p-4 transition-colors',
                form.watch('isCyclesEnabled') && 'bg-primary/[0.04]',
              )}
            >
              <div className="flex items-center gap-2 w-[55%] shrink-0 pt-0.5">
                <Timer className="size-4 text-muted-foreground shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">Cycles</p>
                    {form.watch('isCyclesEnabled') && (
                      <Badge className={onBadgeClass}>On</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Time-boxed sprints for iterative planning
                  </p>
                </div>
              </div>
              <div className="w-[45%] space-y-3">
                <div className="flex items-center gap-3">
                  <Switch
                    id="cycles-toggle"
                    checked={form.watch('isCyclesEnabled')}
                    onCheckedChange={(checked) => {
                      form.setValue('isCyclesEnabled', checked, { shouldDirty: true })
                      if (!checked) {
                        form.setValue('cycleDurationWeeks', null, { shouldDirty: true })
                      }
                      if (checked && project.statusMode !== 'advanced') {
                        // Cycles require advanced status management — enable it automatically.
                        // The server broadcasts the project change on success, so no local
                        // RxDB write is needed (statusMode is server-authoritative).
                        void (async () => {
                          try {
                            const response = await enableProjectAdvancedStatus(workspaceSlug, project.id)
                            toast.success(response.message)
                          } catch (error) {
                            toast.error(
                              error instanceof Error
                                ? error.message
                                : 'Failed to enable advanced status management',
                            )
                            // Revert the cycles toggle since advanced mode failed
                            form.setValue('isCyclesEnabled', false, { shouldDirty: true })
                          }
                        })()
                      }
                    }}
                  />
                  <Label
                    htmlFor="cycles-toggle"
                    className="text-sm text-muted-foreground cursor-pointer"
                  >
                    {form.watch('isCyclesEnabled') ? 'Enabled' : 'Disabled'}
                  </Label>
                </div>
                {form.watch('isCyclesEnabled') && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={52}
                        aria-invalid={Boolean(form.formState.errors.cycleDurationWeeks)}
                        className="h-9 w-20 text-sm"
                        value={form.watch('cycleDurationWeeks') ?? ''}
                        onChange={(e) => {
                          const val = e.target.value
                          form.setValue(
                            'cycleDurationWeeks',
                            val ? Number(val) : null,
                            { shouldDirty: true, shouldValidate: true },
                          )
                        }}
                        placeholder="e.g. 2"
                      />
                      <span className="text-sm text-muted-foreground">weeks</span>
                    </div>
                    {form.formState.errors.cycleDurationWeeks && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.cycleDurationWeeks.message}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Owner */}
            <div
              className={cn(
                'flex items-center p-4 transition-colors',
                form.watch('isAccountableEnabled') && 'bg-primary/[0.04]',
              )}
            >
              <div className="flex items-center gap-2 w-[55%] shrink-0">
                <UserCheck className="size-4 text-muted-foreground shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">Owner</p>
                    {form.watch('isAccountableEnabled') && (
                      <Badge className={onBadgeClass}>On</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Assign a secondary user to provide oversight and ensure the task meets its objectives.
                  </p>
                </div>
              </div>
              <div className="w-[45%] flex items-center gap-3">
                <Switch
                  id="accountable-toggle"
                  checked={form.watch('isAccountableEnabled')}
                  onCheckedChange={(checked) => {
                    form.setValue('isAccountableEnabled', checked, { shouldDirty: true })
                  }}
                />
                <Label
                  htmlFor="accountable-toggle"
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  {form.watch('isAccountableEnabled') ? 'Enabled' : 'Disabled'}
                </Label>
              </div>
            </div>
          </div>
          )}

          {/* Save */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting || !isDirty}
            >
              {form.formState.isSubmitting ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </form>

        {/* MolySA Super Agent activation (gated on the workspace being AI-enabled). Placed
            ABOVE status management so enabling it visibly flips the mode to Advanced and adds
            the AI lane in the sections that follow. */}
        {/* Hidden while Super Agent is off — EXCEPT on a project that already has it
            enabled, which keeps its offboard control (the backend stays live). */}
        {(FEATURE_FLAGS.ENABLE_SUPER_AGENT || project?.superAgentEnabled === true) &&
          !isPersonal && project && workspaceSlug && (
          <SuperAgentSection workspaceSlug={workspaceSlug} projectId={project.id} />
        )}

        {/* GitHub connection: link a repo so PRs/CI/review surface on this project's tasks —
            kept next to Super Agent since agent work runs against the bound repo. */}
        {!isPersonal && project && workspaceSlug && (
          FEATURE_FLAGS.ENABLE_GITHUB_INTEGRATION && (
            <GitHubConnectSection workspaceSlug={workspaceSlug} projectId={project.id} />
          )
        )}

        {/* Status Mode (separate from form — uses its own API) */}
        {!isPersonal && project && (
          <StatusModeSection projectId={project.id} isCyclesEnabled={project.isCyclesEnabled ?? false} workspaceSlug={workspaceSlug} />
        )}

        {/* Statuses used in this project — workspace status membership management */}
        {!isPersonal && project && (
          <ProjectStatusesSection
            workspaceSlug={workspaceSlug}
            projectId={project.id}
            projectStatusMode={project.statusMode}
            statusMigrationInProgress={project.statusMigrationInProgress ?? false}
          />
        )}
          </div>
        </div>
      </div>
    </div>
  )
}

const PROGRESS_TYPE_LABELS: Record<string, string> = {
  [PROGRESS_TYPES.BACKLOG]: 'Backlog',
  [PROGRESS_TYPES.PLANNED]: 'Planned',
  [PROGRESS_TYPES.IN_PROGRESS]: 'In Progress',
  [PROGRESS_TYPES.COMPLETED]: 'Completed',
  [PROGRESS_TYPES.CANCELLED]: 'Cancelled',
}

const SIMPLE_STATUSES = [
  { label: 'Backlog', type: PROGRESS_TYPES.BACKLOG, icon: Circle },
  { label: 'Completed', type: PROGRESS_TYPES.COMPLETED, icon: CheckCircle2 },
]

const ADVANCED_STATUSES = [
  { label: 'Backlog', type: PROGRESS_TYPES.BACKLOG },
  { label: 'Planned', type: PROGRESS_TYPES.PLANNED },
  { label: 'In Progress', type: PROGRESS_TYPES.IN_PROGRESS },
  { label: 'Completed', type: PROGRESS_TYPES.COMPLETED },
  { label: 'Cancelled', type: PROGRESS_TYPES.CANCELLED },
]

function StatusModeSection({ projectId, isCyclesEnabled, workspaceSlug }: { projectId: string; isCyclesEnabled: boolean; workspaceSlug: string }) {
  const { isAdvanced } = useProjectStatusMode(projectId)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [disableDialogOpen, setDisableDialogOpen] = useState(false)
  const [isEnabling, setIsEnabling] = useState(false)
  const [isDisabling, setIsDisabling] = useState(false)

  const statusHook = useStatuses()
  const statuses = statusHook.all.statuses

  const advancedStatuses = useMemo(
    () =>
      statuses.filter((s) => {
        const pt = (s as any).progressType
        return pt !== PROGRESS_TYPES.BACKLOG && pt !== PROGRESS_TYPES.COMPLETED
      }),
    [statuses],
  )

  const [reassignments, setReassignments] = useState<Record<string, 'BL' | 'CD'>>({})

  const { tasks } = useTasks()
  const taskCountsByStatusId = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const task of tasks) {
      if (task.statusId && task.projectId === projectId) {
        counts[task.statusId] = (counts[task.statusId] || 0) + 1
      }
    }
    return counts
  }, [tasks, projectId])

  const handleEnableAdvanced = async () => {
    setIsEnabling(true)
    try {
      const response = await enableProjectAdvancedStatus(workspaceSlug, projectId)
      // No local RxDB write — statusMode is server-authoritative and the server
      // broadcasts the project change to connected clients.
      // Skip the toast on no-op (already enabled) — the broadcast will reconcile
      // any stale client state silently.
      if (!response.noop) {
        toast.success(response.message)
      }
      setConfirmDialogOpen(false)
      logger.logEvent(LogCategory.STATE, 'project_advanced_status_enabled', {
        component: 'ProjectSettingsForm',
        projectId,
      })
    } catch (error) {
      logger.logError(error as Error, LogCategory.API_ERROR, {
        component: 'ProjectSettingsForm',
        action: 'enable_project_advanced_status',
        projectId,
      })
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to enable advanced status management',
      )
    } finally {
      setIsEnabling(false)
    }
  }

  const handleOpenDisableDialog = () => {
    const initial: Record<string, 'BL' | 'CD'> = {}
    for (const status of advancedStatuses) {
      const pt = (status as any).progressType
      initial[status.id] = pt === PROGRESS_TYPES.CANCELLED ? 'CD' : 'BL'
    }
    setReassignments(initial)
    setDisableDialogOpen(true)
  }

  const handleDisableAdvanced = async () => {
    setIsDisabling(true)
    try {
      const reassignmentList = Object.entries(reassignments).map(
        ([statusId, targetProgressType]) => ({
          statusId,
          targetProgressType,
        }),
      )

      const response = await disableProjectAdvancedStatus(workspaceSlug, projectId, {
        reassignments: reassignmentList,
      })
      // No local RxDB write — statusMode/statusMigrationInProgress are
      // server-authoritative and the server broadcasts the project change.
      // Skip the toast on no-op (already simple) — the broadcast will reconcile
      // any stale client state silently.
      if (!response.noop) {
        toast.success(response.message)
      }
      setDisableDialogOpen(false)

      logger.logEvent(LogCategory.STATE, 'project_advanced_status_disabled', {
        component: 'ProjectSettingsForm',
        projectId,
      })
    } catch (error) {
      logger.logError(error as Error, LogCategory.API_ERROR, {
        component: 'ProjectSettingsForm',
        action: 'disable_project_advanced_status',
        projectId,
      })
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to switch to simple status management',
      )
    } finally {
      setIsDisabling(false)
    }
  }

  return (
    <>
      <Separator className="my-8" />
      <div>
        <p className="text-sm font-medium mb-1">Status management</p>
        <p className="text-xs text-muted-foreground mb-4">
          Choose how task progress is tracked in this project
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Simple Card */}
          <Card className={!isAdvanced ? 'border-primary' : ''}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">Simple</CardTitle>
                {!isAdvanced && <Badge variant="secondary">Current</Badge>}
              </div>
              <CardDescription className="text-xs">
                Know what's next and what's done
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-3">
              <ul className="space-y-2">
                {SIMPLE_STATUSES.map((s) => (
                  <li key={s.type} className="flex items-center gap-2">
                    <s.icon className="size-3.5 text-muted-foreground" />
                    <span className="text-sm">{s.label}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            {isAdvanced && (
              <CardFooter className="flex-col items-start gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenDisableDialog}
                  disabled={isCyclesEnabled}
                >
                  Switch to Simple
                </Button>
                {isCyclesEnabled && (
                  <p className="text-xs text-muted-foreground">
                    Disable cycles first to switch to simple mode
                  </p>
                )}
              </CardFooter>
            )}
          </Card>

          {/* Advanced Card */}
          <Card className={isAdvanced ? 'border-primary' : ''}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">Advanced</CardTitle>
                {isAdvanced && <Badge variant="secondary">Current</Badge>}
              </div>
              <CardDescription className="text-xs">
                See where every task stands
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-3">
              <ul className="space-y-2">
                {ADVANCED_STATUSES.map((s) => (
                  <li key={s.type} className="flex items-center gap-2">
                    <StatusIcon progressType={s.type} size="sm" className="shrink-0" />
                    <span className="text-sm">{s.label}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            {!isAdvanced && (
              <CardFooter>
                <Button
                  size="sm"
                  onClick={() => setConfirmDialogOpen(true)}
                >
                  <Zap className="size-3.5" />
                  Enable Advanced
                </Button>
              </CardFooter>
            )}
          </Card>
        </div>
      </div>

      {/* Enable Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable Advanced Status Management?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This will unlock full workflow customisation for this project.
                  Three new progress types will become available:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Planned</strong> — tasks scheduled but not yet started</li>
                  <li><strong>In Progress</strong> — tasks currently being worked on</li>
                  <li><strong>Cancelled</strong> — tasks that have been abandoned</li>
                </ul>
                <p className="text-sm font-medium text-foreground">
                  Switching back will require reassigning tasks to simple statuses.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isEnabling}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleEnableAdvanced} disabled={isEnabling}>
              {isEnabling ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Enabling...
                </>
              ) : (
                'Enable Advanced Mode'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disable / Reassignment Dialog */}
      <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Switch to Simple Status Management</DialogTitle>
            <DialogDescription>
              Tasks using advanced statuses need to be reassigned to either
              Backlog or Completed. Choose where each status's tasks should go.
            </DialogDescription>
          </DialogHeader>

          {advancedStatuses.length > 0 ? (
            <div className="space-y-3 py-2">
              {advancedStatuses.map((status) => {
                const taskCount = taskCountsByStatusId[status.id] || 0
                const pt = (status as any).progressType as string
                return (
                  <div
                    key={status.id}
                    className="flex items-center justify-between gap-3 rounded-md border p-3"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <StatusIcon progressType={pt} statusId={status.id} size="sm" className="shrink-0" />
                      <span className="text-sm font-medium truncate">{status.name}</span>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {PROGRESS_TYPE_LABELS[pt] || pt}
                      </Badge>
                      {taskCount > 0 && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          {taskCount} {taskCount === 1 ? 'task' : 'tasks'}
                        </Badge>
                      )}
                    </div>
                    <Select
                      value={reassignments[status.id] || 'BL'}
                      onValueChange={(value: string) =>
                        setReassignments((prev) => ({
                          ...prev,
                          [status.id]: value as 'BL' | 'CD',
                        }))
                      }
                    >
                      <SelectTrigger className="w-[140px] h-8 shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BL">Backlog</SelectItem>
                        <SelectItem value="CD">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-2">
              No advanced statuses to reassign. You can switch back safely.
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={isDisabling}>Cancel</Button>
            </DialogClose>
            <Button onClick={handleDisableAdvanced} disabled={isDisabling}>
              {isDisabling ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Switching...
                </>
              ) : (
                'Switch to Simple'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
